import React from 'react';
import PropTypes from 'prop-types';
import PersonalizedPlacement from './PersonalizedPlacement/index';
import Location from 'utils/Location';
import cookieUtils from 'utils/Cookies';

/**
 * Higher-Order Component that adds PersonalizedPlacement behavior to any component
 * @param {React.Component} WrappedComponent - The component to wrap with personalization
 * @returns {React.Component} - The wrapped component with personalization capabilities
 */
const withPersonalizedPlacement = WrappedComponent => {
    class WithPersonalizedPlacement extends React.Component {
        constructor(props) {
            super(props);
            this.state = {
                isPreview: false
            };
        }

        componentDidMount() {
            const isPreviewEnv = cookieUtils.read(cookieUtils.KEYS.IS_PREVIEW_ENV_COOKIE);

            if (isPreviewEnv) {
                this.setState({ isPreview: isPreviewEnv });
            }
        }

        /**
         * Determines if PersonalizedPlacement should be rendered
         * @returns {boolean} - Whether to show the personalization overlay
         */
        shouldShowPersonalizedPlacement() {
            const { personalization } = this.props;

            if (this.props.personalization?.isEnabled && this.props.promo && !this.props?.isNBOEnabled) {
                return false;
            }

            return (
                (personalization?.isEnabled || this.props?.isNBCEnabled || this.props?.isNBOEnabled) &&
                Location.isHomepage() &&
                this.state?.isPreview &&
                Sephora?.configurationSettings?.isPreviewPersonalizationEnabled
            );
        }

        /**
         * Renders the PersonalizedPlacement overlay
         * @returns {React.Element|null} - The PersonalizedPlacement component or null
         */
        renderPersonalizedPlacement() {
            if (!this.shouldShowPersonalizedPlacement()) {
                return null;
            }

            const mergedProps = this.props?.personalizedComponent?.variationData || this.props;

            const {
                isNBCEnabled,
                isNBOEnabled,
                isPersistentBanner,
                nonTargettedClick,
                personalization,
                activePage,
                nonTargettedPreviewIndex,
                nonTargettedVariations,
                personalizedComponent,
                position
            } = this.props;

            return (
                <PersonalizedPlacement
                    isPersistentBanner={isPersistentBanner}
                    nonTargettedClick={nonTargettedClick}
                    contextId={personalization?.context}
                    isNBCEnabled={isNBCEnabled}
                    isNBOEnabled={isNBOEnabled}
                    sid={this.props.currentActiveBannerSid || mergedProps.sid} // use for carousel page title
                    activePage={activePage || position + 1}
                    nonTargettedPreviewIndex={nonTargettedPreviewIndex}
                    nonTargettedVariations={nonTargettedVariations}
                    personalizedComponent={personalizedComponent?.p13n}
                    wrappedComponentProps={this.props}
                />
            );
        }

        render() {
            return (
                <div style={{ position: 'relative' }}>
                    {this.renderPersonalizedPlacement()}
                    <WrappedComponent {...this.props} />
                </div>
            );
        }
    }

    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    WithPersonalizedPlacement.displayName = `withPersonalizedPlacement(${displayName})`;

    //  prop types for the HOC
    WithPersonalizedPlacement.propTypes = {
        // Personalization props
        personalization: PropTypes.object,
        isNBCEnabled: PropTypes.bool,
        isNBOEnabled: PropTypes.bool,
        isPersistentBanner: PropTypes.bool,
        nonTargettedClick: PropTypes.func,
        sid: PropTypes.string,
        activePage: PropTypes.number,
        nonTargettedPreviewIndex: PropTypes.number,
        nonTargettedVariations: PropTypes.array,
        personalizedComponent: PropTypes.array,

        ...WrappedComponent.propTypes
    };

    // default props for the HOC
    WithPersonalizedPlacement.defaultProps = {
        isNBCEnabled: false,
        isNBOEnabled: false,
        isPersistentBanner: false,
        nonTargettedClick: null,
        sid: null,
        activePage: null,
        nonTargettedPreviewIndex: -1,
        nonTargettedVariations: [],
        personalizedComponent: null,

        // Pass through all other default props to the wrapped component
        ...WrappedComponent.defaultProps
    };

    return WithPersonalizedPlacement;
};

export default withPersonalizedPlacement;
